import { computed, DestroyRef, DOCUMENT, inject, Injectable, signal } from '@angular/core';

import { OcpTableDataConfig, SortDirection } from './ocp-table-data.interfaces';
import { TableColumnWidthsStore } from '../table-column-widths/table-column-widths.store';

/** Whether a table's columns are still responsive (`%`, recomputed on viewport resize) or pinned to `px` after a manual column resize. */
export type ColumnWidthMode = 'percentage' | 'pixel';

@Injectable()
export class OcpTableData<T> {
  /** Kill switch for persisting/restoring column widths via `TableColumnWidthsStore`; flip off to disable. */
  private static readonly COLUMN_WIDTH_PERSISTENCE_ENABLED = true;

  private readonly widthsStore = inject(TableColumnWidthsStore);
  private readonly document = inject(DOCUMENT);

  private readonly _items = signal<T[]>([]);
  private readonly _sortField = signal<string | null>(null);
  private readonly _sortDirection = signal<SortDirection>('asc');
  private readonly _selectedIds = signal<Set<string>>(new Set());
  private readonly _columnSizing = signal<Record<string, number>>({});
  private readonly _resizingField = signal<string | null>(null);
  private readonly _widthMode = signal<ColumnWidthMode>('percentage');
  private readonly _columnWidthPercentages = signal<Record<string, string>>({});

  private _comparator: OcpTableDataConfig<T>['comparator'] = items => items;
  private _idAccessor: OcpTableDataConfig<T>['idAccessor'] = () => '';
  private _isSelectable: NonNullable<OcpTableDataConfig<T>['isSelectable']> = () => true;
  private _tableId: string | undefined;

  readonly items = this._items.asReadonly();
  readonly sortField = this._sortField.asReadonly();
  readonly sortDirection = this._sortDirection.asReadonly();
  readonly selectedIds = this._selectedIds.asReadonly();
  readonly columnSizing = this._columnSizing.asReadonly();
  readonly resizingField = this._resizingField.asReadonly();
  /** Current column-sizing strategy for this table instance; one `OcpTableData` per table, so this state isn't shared across tables. */
  readonly widthMode = this._widthMode.asReadonly();
  /** `%` widths carried over from the last pixel-mode layout, keeping each column's user-set proportion after a viewport resize drops the `px` pinning. */
  readonly columnWidthPercentages = this._columnWidthPercentages.asReadonly();

  constructor() {
    // Once a column has been manually resized, its siblings are pinned to `px` and stop
    // reshaping proportionally on viewport resize. Resizing the app/window while in that
    // state re-enables the "nice" proportional `%` layout by dropping the pixel overrides.
    const window = this.document.defaultView;
    if (window) {
      const onWindowResize = (): void => {
        if (this._widthMode() === 'pixel') {
          this.resetToPercentageWidths();
        }
      };
      window.addEventListener('resize', onWindowResize);
      inject(DestroyRef).onDestroy(() => window.removeEventListener('resize', onWindowResize));
    }
  }

  readonly sortedItems = computed(() => {
    const field = this._sortField();
    if (!field) return this._items();
    return this._comparator(this._items(), field, this._sortDirection());
  });

  private readonly selectableItems = computed(() =>
    this._items().filter(item => this._isSelectable(item))
  );

  readonly allSelected = computed(() => {
    const items = this.selectableItems();
    const selected = this._selectedIds();
    return items.length > 0 && items.every(item => selected.has(this._idAccessor(item)));
  });

  readonly someSelected = computed(() => {
    const items = this.selectableItems();
    const selected = this._selectedIds();
    const selectedCount = items.filter(item => selected.has(this._idAccessor(item))).length;
    return selectedCount > 0 && selectedCount < items.length;
  });

  configure(config: OcpTableDataConfig<T>): void {
    this._comparator = config.comparator;
    this._idAccessor = config.idAccessor;
    this._isSelectable = config.isSelectable ?? (() => true);
    this._tableId = config.tableId;

    if (config.initialSortField) {
      this._sortField.set(config.initialSortField);
    }
    if (config.initialSortDirection) {
      this._sortDirection.set(config.initialSortDirection);
    }
    if (this._tableId && OcpTableData.COLUMN_WIDTH_PERSISTENCE_ENABLED) {
      const percentages = this.widthsStore.getWidths(this._tableId);
      if (Object.keys(percentages).length > 0) {
        this._columnWidthPercentages.set(this.toPercentageStrings(percentages));
      }
    }
  }

  setItems(items: T[]): void {
    this._items.set(items);
  }

  sort(field: string): void {
    if (this._sortField() === field) {
      this._sortDirection.update(dir => (dir === 'asc' ? 'desc' : 'asc'));
    } else {
      this._sortField.set(field);
      this._sortDirection.set('asc');
    }
  }

  setSortField(field: string): void {
    this._sortField.set(field);
  }

  setSortDirection(direction: SortDirection): void {
    this._sortDirection.set(direction);
  }

  toggleItemSelection(id: string): void {
    this._selectedIds.update(current => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  toggleSelectAll(): void {
    if (this.allSelected()) {
      this.deselectAll();
    } else {
      this.selectAll();
    }
  }

  selectAll(): void {
    const ids = new Set(this.selectableItems().map(item => this._idAccessor(item)));
    this._selectedIds.set(ids);
  }

  deselectAll(): void {
    this._selectedIds.set(new Set());
  }

  isSelected(id: string): boolean {
    return this._selectedIds().has(id);
  }

  deselectRow(id: string): void {
    this._selectedIds.update(current => {
      if (!current.has(id)) {
        return current;
      }

      const next = new Set(current);
      next.delete(id);
      return next;
    });
  }

  /** User-driven resize (drag/keyboard): updates sizing and persists it (as `%` of the table's total width) for future sessions. */
  setColumnWidth(field: string, widthPx: number): void {
    this._columnSizing.update(current => ({ ...current, [field]: widthPx }));
    this._widthMode.set('pixel');
    if (this._tableId && OcpTableData.COLUMN_WIDTH_PERSISTENCE_ENABLED) {
      const percentages = this.computeColumnPercentages(this._columnSizing());
      if (Object.keys(percentages).length > 0) {
        this.widthsStore.setWidths(this._tableId, percentages);
      }
    }
  }

  /** Internal pixel-baseline measurement (see `initializeColumnPixelWidths`); never persisted. */
  freezeColumnWidth(field: string, widthPx: number): void {
    this._columnSizing.update(current => ({ ...current, [field]: widthPx }));
    this._widthMode.set('pixel');
  }

  /**
   * Drops all pixel overrides so columns fall back to `%` widths, but first converts the
   * current pixel widths into proportional percentages so the user's resized proportions are
   * kept instead of reverting to the hardcoded configured defaults.
   */
  resetToPercentageWidths(): void {
    const percentages = this.computeColumnPercentages(this._columnSizing());
    if (Object.keys(percentages).length > 0) {
      this._columnWidthPercentages.set(this.toPercentageStrings(percentages));
    }

    this._columnSizing.set({});
    this._widthMode.set('percentage');
  }

  /** Converts a snapshot of pixel widths into each column's percentage (0-100) of their combined total. */
  private computeColumnPercentages(sizing: Record<string, number>): Record<string, number> {
    const total = Object.values(sizing).reduce((sum, widthPx) => sum + widthPx, 0);
    if (total <= 0) return {};

    const percentages: Record<string, number> = {};
    for (const [field, widthPx] of Object.entries(sizing)) {
      percentages[field] = (widthPx / total) * 100;
    }
    return percentages;
  }

  private toPercentageStrings(percentages: Record<string, number>): Record<string, string> {
    const strings: Record<string, string> = {};
    for (const [field, percent] of Object.entries(percentages)) {
      strings[field] = `${percent}%`;
    }
    return strings;
  }

  startColumnResize(field: string): void {
    this._resizingField.set(field);
  }

  endColumnResize(): void {
    this._resizingField.set(null);
  }
}
